import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "../../components/ui/PageHeader";
import { ErrorText, Panel } from "../../components/ui/feedback";
import { useI18n } from "../../i18n/I18nProvider";
import { formatBytes } from "../../lib/format";
import { usageQuery } from "../workspace/queries";
import { UsageBar } from "./UsageBar";

export interface UsagePageProps {
  workspaceId: string;
}

/** Storage and people against the workspace quotas, as rink-length bars. */
export function UsagePage({ workspaceId }: UsagePageProps) {
  const { t } = useI18n();
  const usage = useQuery(usageQuery(workspaceId));

  if (usage.error) return <ErrorText error={usage.error} />;
  if (!usage.data) return <p className="text-note text-ink-2">{t("app.loading")}</p>;
  const data = usage.data;

  return (
    <div className="space-y-6">
      <PageHeader title={t("usage.title")} />
      <Panel className="space-y-8">
        <UsageBar
          label={t("usage.bytes")}
          used={data.bytesUsed}
          quota={data.quotaBytes}
          format={formatBytes}
          fallback={t("usage.noQuota", { used: formatBytes(data.bytesUsed) })}
        />
        <UsageBar
          label={t("usage.persons")}
          used={data.persons.used}
          quota={data.persons.quota}
          format={(value) => String(value)}
          unit={t("usage.unitPeople")}
          fallback={t("usage.noQuota", { used: String(data.persons.used) })}
        />
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-hairline pt-6">
          <h2 className="text-operate font-semibold text-ink">{t("usage.configurations")}</h2>
          <p className="text-note text-ink-2">
            {t("usage.noQuota", { used: String(data.configurations.used) })}
          </p>
        </div>
      </Panel>
    </div>
  );
}
